<script lang="ts" setup>
import { computed, defineExpose, defineProps, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useBasicLayout } from "@/hooks/useBasicLayout";
import { ai_dictionary_Regexp_list } from "@/views/AIGenerate/smartReading/defaultData";
import { modalController } from "@ionic/vue";
import { handleDecodeUrlComponentStr } from "@/views/AIGenerate/smartReading/commonMethod";
import { audioPlayStream } from "@/js/common";
import AudioComp from "@/views/AIGenerate/components/AudioComp.vue";
import { getBrandImageUrl } from "@/js/brandImages";
import SmartAnalysis from "@/views/AIGenerate/smartAnalysis/smartAnalysis.vue";
import RootsAndAffixes from "@/views/AIGenerate/components/RootsAndAffixes.vue";

interface Props {
  inversion?: boolean;
  error?: boolean;
  text?: Object;
  loading?: boolean;
  asRawText?: boolean;
  propWord?: string;
  show?: boolean;
  learnType?: string;
  fontStyle?: Record<string, any>;
  articleText?: string;
  generateType?: string;
}

interface DictionaryData {
  单词?: string;
  音标?: string;
  中文?: string;
  常用搭配?: Array<{
    短语: string;
    翻译: string;
  }>;
  经典例句?: Array<{
    例句: string;
    翻译: string;
  }>;
  词根?: string;
  词根描述?: string;
  词根例词?: Array<{
    单词: string;
    翻译: string;
  }>;
  前缀?: string;
  前缀描述?: string;
  前缀例词?: Array<{
    单词: string;
    翻译: string;
  }>;
  后缀?: string;
  后缀描述?: string;
  后缀例词?: Array<{
    单词: string;
    翻译: string;
  }>;
  词缀?: string; // 添加词缀字段
}

const gotoIcon = getBrandImageUrl("ai_smart_reading/analysis_icon.svg");
const props = defineProps<Props>();
const route = useRoute();
const { isMobile } = useBasicLayout();
const gptStr = ref("");
const studentInfo = ref();
const audioPlayer = ref();
const mediaType = ref();
const dictionaryData = ref<DictionaryData | any>({});

const textRef = ref<HTMLElement>();

const textBlack = ref<any>();
const bofangIcon = ref<Record<string, string>>({});
const timer = ref(0);
const dicObj = ref<Record<string, any>>({});

watch(
  () => props.text,
  (newVal) => {
    if (newVal) {
      try {
        dictionaryData.value = { ...newVal };
      } catch (e) {
        console.error("Failed to parse dictionary data:", e);
      }
    }
  },
  { immediate: true }
);

const loading = computed(() => props.loading);

const wrapClass = computed(() => {
  return [
    "text-wrap",
    "min-w-[20px]",
    "rounded-md",
    isMobile.value ? "p-2" : "px-3 py-2",
    props.inversion ? "bg-[#d2f9d1]" : "bg-[#f4f6f8]",
    props.inversion ? "dark:bg-[#a1dc95]" : "dark:bg-[#1e1e20]",
    props.inversion ? "message-request" : "message-reply",
    { "text-red-500": props.error },
  ];
});
const text = computed(() => {
  // 给文章添加标题并首行居中显示
  let value;
  if (props.generateType === "vocabulary") {
    value = props.text ?? "";
  } else {
    value = "# " + props.text ?? "";
  }
  if (!props.asRawText) {
    value = value.replace(/句子\n/g, "句子:");
    value = value.replace(/句子[:：]\n/g, "句子:");
    return mdi.render(value);
  }
  return value;
});

async function gotoAnalysis(value: string) {
  const modal = await modalController.create({
    component: SmartAnalysis,
    componentProps: { question: value },
    backdropDismiss: false,
    cssClass: "modal_controller_class",
  });
  await modal.present();
  const { data } = await modal.onDidDismiss();
  if ("cancel" === data) {
    await modal.dismiss();
  }
}

function getEnByCn(cn: string) {
  const item = ai_dictionary_Regexp_list.find((i) => cn.includes(i.cn));
  if (item) {
    return item.en;
  } else {
    return "";
  }
}

// 暂停播放
function pauseAudio(id?: string) {
  if (audioPlayer.value) {
    if (mediaType.value === "MEDIA_SOURCE") {
      audioPlayer.value.pause();
      audioPlayer.value = null;
    } else if (mediaType.value === "AUDIO_CONTEXT") {
      audioPlayer.value.close();
      audioPlayer.value = null;
    }
  }
  if (id) {
    bofangIcon.value[id] = "icon-bofang3";
    setElementClass(id, "icon-bofang3");
  } else {
    bofangIcon.value = {};
  }
  clearInterval(timer.value);
}

// 设置播放按钮元素类名
function setElementClass(id: string, value: string) {
  const el = document.querySelector(`#${id}`);
  const classList = el?.classList;
  classList?.remove("icon-bofang3", "icon-bofang2", "icon-bofang1");
  classList?.add(value);
}

// 播放音频
function streamAudioPlay(txt: string, id: string) {
  // 关闭音频
  pauseAudio(id);

  audioPlayStream(txt, (callbackData: any, type: string) => {
    mediaType.value = type;
    let num = 0;
    if (mediaType.value === "MEDIA_SOURCE") {
      audioPlayer.value = new Audio(callbackData);
      audioPlayer.value.oncanplay = function () {
        audioPlayer.value.play();
      };
      audioPlayer.value.onended = function () {
        console.log("ended");
        pauseAudio(id);
      };
      audioPlayer.value.oncanplaythrough = () => {
        console.log("loading");
        clearInterval(timer.value);
        timer.value = window.setInterval(() => {
          num = ++num % 3;
          bofangIcon.value[id] = `icon-bofang${num + 1}`;
          setElementClass(id, `icon-bofang${num + 1}`);
        }, 300);
      };
    } else if (mediaType.value === "AUDIO_CONTEXT") {
      audioPlayer.value = callbackData;
    }
  });
}

async function clickRootOrAffix(name: any) {
  if (!dictionaryData.value[name + "例词"].length) {
    return;
  }
  const modal = await modalController.create({
    component: RootsAndAffixes,
    componentProps: {
      name: name + "串记",
      resJson: {
        wordRoot: dictionaryData.value[name],
        words: dictionaryData.value[name + "例词"],
        rootphonetic: dictionaryData.value[name + "音标"],
        rootchinesedefinition: dictionaryData.value.中文,
      },
    },
    initialBreakpoint: 0.6,
    cssClass: "down_modal_class",
  });
  await modal.present();
  const { data } = await modal.onDidDismiss();
  if (data === "cancel") {
    await modal.dismiss();
  }
}

onMounted(() => {
  //获取路由参数
  studentInfo.value = handleDecodeUrlComponentStr(route.params.studentInfo);
  console.log(dicObj.value);
});

onBeforeUnmount(() => {
  console.log("onBeforeUnmount");
  pauseAudio();
});

//对外部暴露函数
defineExpose({
  textBlack,
  pauseAudio,
});
</script>

<template>
  <div class="text-black" :class="wrapClass">
    <div ref="textRef" class="leading-relaxed break-words">
      <div
        v-if="dictionaryData.音标 && dictionaryData.音标 && dictionaryData.中文"
        ref="textBlack"
        :scroll-events="true"
        :overflow-scroll="true"
        :scroll="true"
      >
        <div
          class="markdown-body4"
          :class="{ 'common-markdown': props.generateType === 'vocabulary' }"
        >
          <!-- 单词标题部分 -->
          <div v-if="dictionaryData.单词 || dictionaryData.音标" class="center-title">
            <h1 v-if="dictionaryData.单词" class="center-title-top">
              {{ dictionaryData.单词 }}
            </h1>
            <div v-if="dictionaryData.音标" class="center-title-bottom">
              [{{ dictionaryData.音标 }}]
              <AudioComp :word="dictionaryData.单词 || ''" color="#101214" />
            </div>
          </div>

          <!-- 中文释义 -->
          <div v-if="dictionaryData.中文" class="dictionary-item">
            <span class="normal-title">中文</span>
            <span class="ai-value">{{ dictionaryData.中文 }}</span>
          </div>

          <!-- 词根部分 -->
          <div v-if="dictionaryData.词根 && dictionaryData.词根例词.length" class="dictionary-item">
            <div class="ai-value-root" @click="clickRootOrAffix('词根')">
              <span
                class="ai-button"
                :class="{ 'ai-button-disabled': !dictionaryData.词根例词.length }"
                id="ai-button-root"
                data-text="词根"
                >词根</span
              >
              <span class="ai-value">{{ dictionaryData.词根 }}</span>
              <span v-if="dictionaryData.词根描述" class="ai-tip">
                {{ dictionaryData.词根描述 }}
              </span>
            </div>
          </div>

          <!-- 前缀部分 -->
          <div v-if="dictionaryData.前缀 && dictionaryData.前缀例词.length" class="dictionary-item">
            <div class="ai-value-affix" @click="clickRootOrAffix('前缀')">
              <span
                class="ai-button"
                :class="{ 'ai-button-disabled': !dictionaryData.前缀例词.length }"
                id="ai-button-affix"
                data-text="前缀"
                >前缀</span
              >
              <span class="ai-value">{{ dictionaryData.前缀 }}</span>
              <span v-if="dictionaryData.前缀描述" class="ai-tip">
                {{ dictionaryData.前缀描述 }}
              </span>
            </div>
          </div>

          <!-- 后缀部分 -->
          <div v-if="dictionaryData.后缀 && dictionaryData.后缀例词.length" class="dictionary-item">
            <div class="ai-value-affix" @click="clickRootOrAffix('后缀')">
              <span
                class="ai-button"
                :class="{ 'ai-button-disabled': !dictionaryData.后缀例词.length }"
                id="ai-button-affix"
                data-text="后缀"
                >后缀</span
              >
              <span class="ai-value">{{ dictionaryData.后缀 }}</span>
              <span v-if="dictionaryData.后缀描述" class="ai-tip">
                {{ dictionaryData.后缀描述 }}
              </span>
            </div>
          </div>

          <!-- 常用搭配 -->
          <div v-if="dictionaryData.常用搭配?.length" class="dictionary-item">
            <span class="special-title">常用搭配</span>
            <div class="word-examples">
              <div
                v-for="(item, index) in dictionaryData.常用搭配"
                :key="index"
                class="example-item"
              >
                <span class="ai-value">{{ item.短语 }}</span>
                <span class="ai-tip">{{ item.翻译 }}</span>
              </div>
            </div>
          </div>

          <!-- 经典例句 -->
          <div v-if="dictionaryData.经典例句?.length" class="dictionary-item">
            <span class="special-title">经典例句</span>
            <div class="word-examples">
              <div
                v-for="(item, index) in dictionaryData.经典例句"
                :key="index"
                class="example-item"
              >
                <div class="example-item-detail">
                  <div class="audio-com">
                    <AudioComp :word="item.例句 || ''" color="#101214" />
                  </div>
                  <img class="goto-icon" @click="gotoAnalysis(item.例句)" :src="gotoIcon" alt="" />
                  <span class="ai-value">{{ item.例句 }}</span>
                </div>

                <div class="ai-tip">{{ item.翻译 }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="no-ai-data"
        v-if="!(dictionaryData.音标 && dictionaryData.音标 && dictionaryData.中文) && !loading"
      >
        抱歉，没有找到相关信息。
      </div>
    </div>
  </div>
</template>

<style lang="less" scoped>
@import "@/styles/variables.less";

.ai-sentence {
  //background-color: #1f6feb;
}

html.plt-mobile ion-app {
  -webkit-user-select: auto !important;
  -moz-user-select: auto !important;
  -ms-user-select: auto !important;
  user-select: auto !important;
}

ion-content::part(background) {
  background-color: transparent;
}

.flex-row {
  display: flex;
  flex-direction: row;
}

.text-black {
  //position: relative;
  font-size: inherit;
  display: flex;
  width: 100%;
  height: 100%;

  .leading-relaxed {
    width: 100%;

    .no-ai-data {
      font-size: 1rem;
      color: #291c0a;
    }
  }

  .markdown-body4 {
    width: 100%;
    padding: 0 0.8rem;

    .center-title {
      width: 100%;

      .center-title-top {
        width: 100%;
        word-wrap: break-word;
        color: #693a1f;
      }
    }
  }

  .cursor-pointer {
    cursor: pointer;
  }

  .handle-methods {
    z-index: 1;
    position: absolute;
    top: var(--currentX);
    left: var(--currentY);
  }
}

.markdown-body4 {
  background-color: transparent;
  font-size: inherit;
  width: 100%;
  line-height: 2rem;
  height: 100%;

  &::part(scroll) {
    width: calc(100% + 4vw);
    height: 100%;
    display: flex;
    flex-direction: column;
    scrollbar-width: none;
    -ms-overflow-style: none;
    padding-right: 4vw;
  }

  .center-title {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-bottom: 0.1rem solid #eee8e3;
    margin-bottom: 1.35rem;

    &-top {
      font-weight: 600;
      font-size: 2.02rem;
      margin: 0 0 0.5rem 0;
    }

    &-bottom {
      background: #fccda2;
      border-radius: 1.5rem;
      color: #101214;
      padding: 0.2rem 0.1rem 0.4rem 0.6rem;
      font-size: 0.81rem;
      margin: 0 0 1.5rem;
      line-height: 1.35rem;
      .bofang-icon {
        width: 1.21rem;
      }
    }
  }

  .ai-empty {
    margin-bottom: -2rem;
    height: 0;
    display: block;
    font-size: 0;
  }

  .ai-button {
    margin-left: 0 !important;
    padding: 0 !important;
    text-align: center;
    white-space: nowrap;
    font-size: 0.95rem;
    border-radius: 1rem;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    margin-right: 1rem;
    position: relative;
    line-height: 1.6;
    z-index: 2;

    &::after {
      content: attr(data-text);
      display: block;
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      border-radius: 1rem;
      width: 2.97rem;
      height: 1.55rem;
      background: #ffe7cb;
      color: #c07935;
      z-index: 1;
    }

    &-icon {
      margin-right: 0.2rem;
      font-size: 0.8rem;
      margin-top: 0.1rem;
    }

    &-disabled {
      width: 3.5rem;
      height: 1.8rem;
      border-radius: 1rem;
      background: #c8c9cc;
      color: #ffffff;
      cursor: not-allowed;
    }
  }

  .ai-value {
    color: #291c0a;
    font-size: 1.01rem;
    font-weight: 400;
    margin-right: 0.5rem;
  }

  .ai-tip,
  .ai-text {
    color: #938f87;
    font-size: 0.95rem;
    font-weight: 400;
  }

  .ai-label {
    color: #c07935;
    font-size: 1.1rem;
    font-weight: 400;
    margin-right: 1rem;
    margin-top: 2rem;
  }

  .bofang-icon {
    line-height: 16px;
    vertical-align: middle;
    font-size: 5vw;
  }

  h1 {
    text-align: center;
    font-size: var(--font-3-point-5);
    font-weight: 700;
    padding: 0 1rem;
    white-space: pre-wrap;

    strong {
      color: #262626 !important;
      font-weight: 700;
    }
  }

  p {
    font-size: 1rem;
    white-space: pre-wrap;
    text-indent: 5vw;
    line-height: 2rem;
    margin: 0;
  }

  ol {
    list-style-type: decimal;
  }

  ul {
    margin: 0;
    padding-left: 0;
  }

  li,
  .dictionary-item {
    list-style: none;

    .ai-label {
      position: relative;
      display: block;
    }
  }

  table {
    border-collapse: collapse;
    border: 2px solid rgb(140 140 140);
    font-family: sans-serif;
    font-size: 0.8rem;
    letter-spacing: 1px;
  }

  caption {
    caption-side: bottom;
    padding: 10px;
    font-weight: bold;
  }

  thead,
  tfoot {
    background-color: rgb(228 240 245);
  }

  th,
  td {
    border: 1px solid rgb(160 160 160);
  }

  th {
    padding: 0;
  }

  td {
    padding: 1vw;
  }

  td:last-of-type {
    text-align: center;
  }

  tbody > tr:nth-of-type(even) {
    background-color: rgb(237 238 242);
  }

  tbody > tr:nth-of-type(odd) {
    background-color: #ffffff;
  }

  tfoot th {
    text-align: right;
  }

  tfoot td {
    font-weight: bold;
  }

  pre code,
  pre tt {
    line-height: 1.65;
  }

  .highlight pre,
  pre {
    background-color: #fff;
  }

  code.hljs {
    padding: 0;
  }

  .code-block {
    &-wrapper {
      position: relative;
      padding-top: 24px;
    }

    &-header {
      position: absolute;
      top: 5px;
      right: 0;
      width: 100%;
      padding: 0 1rem;
      display: flex;
      justify-content: flex-end;
      align-items: center;
      color: #b3b3b3;

      &__copy {
        cursor: pointer;
        margin-left: 0.5rem;
        user-select: none;

        &:hover {
          color: #65a665;
        }
      }
    }
  }
}

.special-title {
  color: #693a1f;
  width: 100%;
  height: 2.3rem;
  font-size: 1.01rem;
  font-weight: 600;
  margin-right: 1rem;
  margin-top: 1.89rem;
  position: relative;
  display: inline-block;
  &::after {
    content: "";
    width: 2.02rem;
    height: 2.02rem;
    position: absolute;
    left: -0.3rem;
    top: -0.3rem;
    display: block;
    background: url("@/assets/ai/dictionary_li.png") no-repeat 100% / 100%;
  }
}

.common-markdown {
  p {
    text-indent: 0;
  }
}

html.dark {
  .message-reply {
    .whitespace-pre-wrap {
      white-space: pre-wrap;
      color: var(--n-text-color);
    }
  }

  .highlight pre,
  pre {
    background-color: #282c34;
  }
}

.dictionary-item {
  .normal-title {
    color: #c07935;
    font-size: 0.95rem;
    margin-right: 1rem;
  }

  .word-examples {
    margin-top: 0.5rem;

    .example-item {
      font-size: 0.95rem;
      line-height: 1.6;

      &-detail {
        display: inline;

        .audio-com {
          display: inline-flex;
          justify-content: center;
          width: 1.21rem;
          height: 1.21rem;
          border-radius: 50%;
          background-color: #fccda2;
          vertical-align: middle;
          .bofang-icon {
            display: flex;
            justify-content: center;
            align-items: center;
            font-size: 1.2rem !important;
          }
        }
        .goto-icon {
          width: 1.35rem;
          vertical-align: middle;
          margin: 0 0.4rem;
        }
      }

      .ai-value {
        margin-right: 1rem;
      }
    }
  }
}
</style>
